import type { Course, AppSettings, GPABreakdown } from '@gpa-scales/shared-types';
import { calculateAllGPA } from './gpaCalculations';

export function computeTermBreakdown(
  courses: Course[],
  settings: AppSettings
): GPABreakdown {
  // Group courses by (academicYear, term)
  const groups = new Map<string, Course[]>();
  for (const course of courses) {
    const key = `${course.academicYear}|${course.term}`;
    const existing = groups.get(key) ?? [];
    existing.push(course);
    groups.set(key, existing);
  }

  // Sort chronologically
  const sortedKeys = [...groups.keys()].sort((a, b) => {
    const [yearA, termA] = a.split('|');
    const [yearB, termB] = b.split('|');
    if (yearA !== yearB) return yearA.localeCompare(yearB);
    const termOrder = ['fall', 'q1', 't1', 'winter', 'q2', 'spring', 'q3', 't2', 'summer', 'q4', 't3'];
    return (termOrder.indexOf(termA) ?? 99) - (termOrder.indexOf(termB) ?? 99);
  });

  // Per-term breakdown
  const byTerm = sortedKeys.map((key) => {
    const [academicYear, term] = key.split('|');
    const termCourses = groups.get(key) ?? [];
    const variants = calculateAllGPA(termCourses, settings);
    const gpa: Record<string, number> = {};
    for (const v of variants) {
      gpa[v.label] = v.value;
    }
    return { term: term as GPABreakdown['byTerm'][number]['term'], academicYear, gpa };
  });

  // Cumulative breakdown
  const cumulative: GPABreakdown['cumulative'] = [];
  const seenCourses: Course[] = [];
  const uniqueYears = [...new Set(sortedKeys.map((k) => k.split('|')[0]))].sort();

  for (const year of uniqueYears) {
    const yearCourses = courses.filter((c) => c.academicYear === year);
    seenCourses.push(...yearCourses);
    const variants = calculateAllGPA(seenCourses, settings);
    const gpa: Record<string, number> = {};
    for (const v of variants) {
      gpa[v.label] = v.value;
    }
    cumulative.push({ academicYear: year, gpa });
  }

  return { byTerm, cumulative };
}
